/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, FormHelperText } from '@mui/material';
import PageSection from '@product-framework/components/PageSection';
import {
  FORM_ACTION,
  UnSavedChangesDialog,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  FormFields,
  IconLibrary,
  useAvailabilitySlotTypes,
  PermissionButton,
} from '@product-framework/index';
import editSchema from './schema/edit.json';
import {
  DEFAULT_SCHEDULE_CURRENT_ID,
  CURRENT_VIEW,
} from '../../utils/constants';
import RecurringSpecialSchedule from './components/RecurringSpecialSchedule';
import SpecialSchedule from '../../utils/SpecialSchedule';
import validationSchema, { nonSchemaValidations } from './validations';

const FORM_ID = 'FORM_ID_AVAILABILITY_SP_SPECIAL_SCHEDULE';

function Header({ currentAction, readOnly }) {
  if (FORM_ACTION.CREATE === currentAction) {
    return (
      <FormattedMessage id="CREATE_SCHEDULE" defaultMessage="Create Schedule" />
    );
  }
  if (readOnly) {
    return (
      <FormattedMessage id="VIEW_SCHEDULE" defaultMessage="View Schedule" />
    );
  }
  return <FormattedMessage id="EDIT_SCHEDULE" defaultMessage="Edit Schedule" />;
}

function SpecialScheduleForm({
  params,
  currentView,
  setCurrentView,
  selectedScheduleId,
  setSelectedScheduleId,
  readOnly,
  locationId = null,
  serviceAreaId = null,
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const formRef = useRef();
  const { slotTypes } = useAvailabilitySlotTypes();
  const serviceProviderId = params?.pageId;
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [currentAction, setCurrentAction] = useState(
    selectedScheduleId ? FORM_ACTION.EDIT : FORM_ACTION.CREATE,
  );

  const onFormExit = () => {
    setSelectedScheduleId(DEFAULT_SCHEDULE_CURRENT_ID);
    setCurrentView(CURRENT_VIEW.DEFAULT);
  };

  const onSubmit = async data => {
    const submissionType = SpecialSchedule.getScheduleSubmissionType({ data });
    const requestObject = SpecialSchedule.getUpsertObject({
      data,
      submissionType,
    });
    const responseSpecialSchedule = await SpecialSchedule.upsert({
      params,
      data: requestObject,
      specialScheduleId: selectedScheduleId,
      locationId,
    });
    if (responseSpecialSchedule?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SPECIAL_SCHEDULE_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'Special Schedule was saved successfully',
        }),
      );
      const nextData = SpecialSchedule.parseSuccessResponseForNextValue({
        schedule: responseSpecialSchedule?.data,
        slotTypes,
      });
      return {
        nextValues: nextData,
        errors: null,
        callback: () => {
          setSelectedScheduleId(nextData?.id);
          setCurrentView(CURRENT_VIEW.EDIT_SCHEDULE);
        },
      };
    }

    let errors = [];
    if (responseSpecialSchedule?.errors) {
      errors = [...responseSpecialSchedule.errors];
    }

    const nextData = SpecialSchedule.parseErrorResponseForNextValue({
      schedule: data,
    });
    return {
      nextValues: nextData,
      errors,
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    if (currentAction === FORM_ACTION.CREATE) {
      const defaultValues = {
        data: SpecialSchedule.getDefaultValues(),
        errors: null,
      };
      return defaultValues;
    }
    // If Form is in Edit mode
    const response = await SpecialSchedule.fetch({
      params,
      specialScheduleId: selectedScheduleId,
      locationId,
    });

    if (response?.ok) {
      const processedData = SpecialSchedule.parseSuccessResponseForNextValue({
        schedule: response?.data,
      });
      const defaultValues = {
        data: processedData,
        errors: null,
      };
      return defaultValues;
    }
    return {
      data: response.data,
      errors: response.errors,
    };
  }, [serviceProviderId, selectedScheduleId, currentView]);

  useEffect(() => {
    setCurrentAction(
      selectedScheduleId ? FORM_ACTION.EDIT : FORM_ACTION.CREATE,
    );
  }, [selectedScheduleId]);

  return (
    <PageSection
      id="ScrollTopElementSpecialScheduleForm"
      subSection
      label={<Header currentAction={currentAction} readOnly={readOnly} />}>
      <Box sx={{ mt: -1, mb: 3 }}>
        <FormHelperText>
          <FormattedMessage
            id="SPECIAL_SCHEDULE_INSTRUCTION_MAIN"
            defaultMessage="Use a Special Schedule to set temporary or recurring date-specific changes to availability, including annual holidays, time off, or one-time availability changes, without changing the Default Schedule."
          />
        </FormHelperText>
        <FormHelperText>
          {locationId ? (
            <FormattedMessage
              id="SPECIAL_SCHEDULE_INSTRUCTION_LOCATION"
              defaultMessage="Special Schedules added at the Location level apply to all Service Areas for the Location unless overridden."
            />
          ) : serviceAreaId ? (
            <FormattedMessage
              id="SPECIAL_SCHEDULE_INSTRUCTION_SERVICE_AREA"
              defaultMessage="Special Schedules added at the Service Area level apply only to the Service Area."
            />
          ) : (
            <FormattedMessage
              id="SPECIAL_SCHEDULE_INSTRUCTION_SERVICE_PROVIDER"
              defaultMessage="Special Schedules added at the Service Provider level apply to all Locations and Service Areas unless overridden."
            />
          )}
        </FormHelperText>
      </Box>
      <Form
        submitWithReadOnlyFields
        formId={FORM_ID}
        ref={formRef}
        onSubmit={onSubmit}
        schema={editSchema}
        staticPermissions={SpecialSchedule.getFieldsWithPermission({
          readOnly,
        })}
        validationSchema={validationSchema(formatMessage)}
        nonSchemaValidations={nonSchemaValidations(formatMessage)}
        customComponents={{
          RecurringSpecialSchedule,
          ...FormFields,
        }}
        fetchDefaultValues={fetchDefaultValues}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            permissionName={
              FORM_ACTION.CREATE === currentAction
                ? 'CAN_CREATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
                : 'CAN_UPDATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
            }
            onClick={() => {
              formRef.current?.submit({ formId: FORM_ID });
            }}>
            <FormattedMessage
              id="SAVE_SPECIAL_SCHEDULE"
              defaultMessage="Save Special Schedule"
            />
          </PermissionButton>
          <PermissionButton
            variant="contained"
            startIcon={<IconLibrary.Cancel />}
            color="primary"
            permissionName={
              FORM_ACTION.CREATE === currentAction
                ? 'CAN_CREATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
                : 'CAN_UPDATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
            }
            onClick={() => {
              if (formRef?.current?.isDirty()) {
                setShowUnsavedChangesDialog(true);
              } else {
                onFormExit();
              }
            }}>
            <FormattedMessage id="CLOSE" defaultMessage="Close" />
          </PermissionButton>
          <PermissionButton
            variant="contained"
            color="primary"
            permissionName={
              FORM_ACTION.CREATE === currentAction
                ? 'CAN_CREATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
                : 'CAN_UPDATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
            }
            startIcon={<IconLibrary.Reset />}
            onClick={() => formRef.current?.reset()}>
            <FormattedMessage id="RESET" defaultMessage="Reset" />
          </PermissionButton>
          {readOnly && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<IconLibrary.Back />}
              onClick={() => {
                setSelectedScheduleId(null);
                setCurrentView(CURRENT_VIEW.DEFAULT);
              }}>
              <FormattedMessage
                id="BACK_TO_LIST"
                defaultMessage="Back to List"
              />
            </Button>
          )}
        </Box>
      </Form>
      <UnSavedChangesDialog
        open={showUnsavedChangesDialog}
        onLeave={onFormExit}
        onStay={() => setShowUnsavedChangesDialog(false)}
      />
    </PageSection>
  );
}

export default SpecialScheduleForm;
